import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from "recharts";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtKwh, monthLabel, fmtNumber } from "@/lib/format";
import { publicFileUrl, API } from "@/lib/api";
import {
  Zap,
  Battery,
  Receipt,
  TrendingUp,
  Sun,
  Loader2,
  PiggyBank,
  Sparkles,
  Leaf,
  Download,
  BarChart3,
} from "lucide-react";

// Deriva a tarifa efetiva (R$/kWh) a partir de uma fatura EDP parseada.
// Prioriza a tarifa REAL da própria conta; senão usa TUSD+TE + impostos "por dentro".
function effectiveTariff(parsed) {
  const p = parsed || {};
  const consumo = Number(p.consumo_kwh || 0);
  const energiaValor = Number(p.valores?.energia_consumida_valor || 0);
  if (energiaValor > 0 && consumo > 0) return energiaValor / consumo;

  const tusd = Number(p.tarifas?.tusd_kwh || 0);
  const te = Number(p.tarifas?.te_kwh || 0);
  if (tusd + te <= 0) return 0;
  const icms = Number(p.impostos?.icms_percentual || 0) / 100;
  const pis = Number(p.impostos?.pis_percentual || 0) / 100;
  const cofins = Number(p.impostos?.cofins_percentual || 0) / 100;
  const totalTaxes = icms + pis + cofins;
  return totalTaxes > 0 && totalTaxes < 1 ? (tusd + te) / (1 - totalTaxes) : tusd + te;
}

// Extrai a chave "YYYY-MM" de uma fatura
function billMonthKey(b) {
  const p = b?.parsed || {};
  if (p.referencia_mes && typeof p.referencia_mes === "string" && p.referencia_mes.includes("/")) {
    const [mm, yyyy] = p.referencia_mes.split("/");
    if (mm && yyyy) return `${yyyy}-${mm.padStart(2, "0")}`;
  }
  if (b?.created_at) return b.created_at.slice(0, 7);
  return null;
}

export default function ClientPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/clients/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "notfound" : "error"));
  }, [slug]);

  const consolidated = useMemo(() => {
    if (!data) return null;
    // Build a monthly map: label -> { month, generation, consumption, saldo, total }
    const map = {};
    (data.generation || []).forEach((g) => {
      const key = g.reference_month;
      if (!map[key]) map[key] = { month: key, generation: 0, consumption: 0, saldo: 0, total: 0 };
      map[key].generation = g.generation_kwh || 0;
    });
    (data.bills || []).forEach((b) => {
      const p = b.parsed || {};
      // referencia_mes might be "MM/YYYY" -> convert to "YYYY-MM"
      let key = null;
      if (p.referencia_mes && typeof p.referencia_mes === "string" && p.referencia_mes.includes("/")) {
        const [mm, yyyy] = p.referencia_mes.split("/");
        if (mm && yyyy) key = `${yyyy}-${mm.padStart(2, "0")}`;
      }
      if (!key && b.created_at) key = b.created_at.slice(0, 7);
      if (!key) return;
      if (!map[key]) map[key] = { month: key, generation: 0, consumption: 0, saldo: 0, total: 0 };
      map[key].consumption = p.consumo_kwh || 0;
      map[key].saldo = p.saldo_creditos_kwh || 0;
      map[key].total = p.valores?.valor_total || 0;
    });
    const rows = Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
    return rows;
  }, [data]);

  if (error === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6] p-8">
        <div className="text-center max-w-md">
          <h1 className="font-display text-3xl font-semibold text-[#1A1A1A]">Cliente não encontrado</h1>
          <p className="text-sm text-[#4A4A4A] mt-3">
            Verifique o link enviado pela sua gestora de energia.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
        <Loader2 className="h-6 w-6 animate-spin text-[#1B4332]" />
      </div>
    );
  }

  const { client, bills } = data;
  const latest = bills?.[0]?.parsed || {};

  const totalGen = (data.generation || []).reduce((s, g) => s + (g.generation_kwh || 0), 0);
  const totalConsumption = (data.bills || []).reduce((s, b) => s + (b.parsed?.consumo_kwh || 0), 0);
  const totalPaid = (data.bills || []).reduce((s, b) => s + (b.parsed?.valores?.valor_total || 0), 0);
  const totalTaxes = (data.bills || []).reduce((s, b) => {
    const imp = b.parsed?.impostos || {};
    return s + (imp.icms_valor || 0) + (imp.pis_valor || 0) + (imp.cofins_valor || 0);
  }, 0);

  const impostosBreakdown = {
    icms: (data.bills || []).reduce((s, b) => s + (b.parsed?.impostos?.icms_valor || 0), 0),
    pis: (data.bills || []).reduce((s, b) => s + (b.parsed?.impostos?.pis_valor || 0), 0),
    cofins: (data.bills || []).reduce((s, b) => s + (b.parsed?.impostos?.cofins_valor || 0), 0),
    cip: (data.bills || []).reduce((s, b) => s + (b.parsed?.impostos?.cip_valor || 0), 0),
  };

  // ===== Cálculo de economia baseado na GERAÇÃO TOTAL da usina =====
  // 1) Indexa faturas por mês (YYYY-MM) e calcula tarifa efetiva de cada uma
  const billByMonth = {};
  (data.bills || []).forEach((b) => {
    const key = billMonthKey(b);
    if (key) billByMonth[key] = { parsed: b.parsed || {}, tariff: effectiveTariff(b.parsed) };
  });
  const tariffValues = Object.values(billByMonth).map((x) => x.tariff).filter((t) => t > 0);
  const avgTariff = tariffValues.length ? tariffValues.reduce((a, b) => a + b, 0) / tariffValues.length : 0;

  // 2) Latest bill month + geração da usina desse mesmo mês
  const latestMonthKey = billMonthKey(bills?.[0]);
  const latestGenRecord = (data.generation || []).find((g) => g.reference_month === latestMonthKey);
  const latestGenKwh =
    Number(latestGenRecord?.generation_kwh || 0) ||
    Number(latest.geracao_kwh || 0) ||
    Number(latest.credito_utilizado_kwh || 0);
  const latestTariff = billByMonth[latestMonthKey]?.tariff || avgTariff;
  const savingsLatest =
    latestGenKwh > 0 && latestTariff > 0
      ? (() => {
          const economia = latestGenKwh * latestTariff;
          const valorAtual = Number(latest.valores?.valor_total || 0);
          const valorSem = valorAtual + economia;
          const economiaPct = valorSem > 0 ? (economia / valorSem) * 100 : 0;
          return {
            economia,
            valorAtual,
            valorSem,
            economiaPct,
            tarifaEfetiva: latestTariff,
            generation: latestGenKwh,
          };
        })()
      : null;

  // 3) Acumulados: soma da geração total × tarifa (do mês correspondente ou tarifa média)
  const useGeneration = (data.generation || []).length > 0 && avgTariff > 0;
  const totalSavings = useGeneration
    ? (data.generation || []).reduce((sum, g) => {
        const t = billByMonth[g.reference_month]?.tariff || avgTariff;
        return sum + Number(g.generation_kwh || 0) * t;
      }, 0)
    : // Fallback quando não há dados de geração: usa créditos utilizados nas contas
      (data.bills || []).reduce((sum, b) => {
        const t = effectiveTariff(b.parsed);
        return sum + Number(b.parsed?.credito_utilizado_kwh || 0) * t;
      }, 0);
  const totalWithoutSolar = totalPaid + totalSavings;
  const totalSavingsKwh = useGeneration
    ? totalGen
    : (data.bills || []).reduce((s, b) => s + Number(b.parsed?.credito_utilizado_kwh || 0), 0);
  const savingsPctAll = totalWithoutSolar > 0 ? (totalSavings / totalWithoutSolar) * 100 : 0;

  // 4) Série mensal de economia (R$) para gráfico de barras
  const monthlySavings = useGeneration
    ? (data.generation || [])
        .map((g) => {
          const t = billByMonth[g.reference_month]?.tariff || avgTariff;
          return {
            month: g.reference_month,
            economia: Math.round((Number(g.generation_kwh || 0) * t) * 100) / 100,
            geracao: Number(g.generation_kwh || 0),
          };
        })
        .sort((a, b) => a.month.localeCompare(b.month))
    : (data.bills || [])
        .map((b) => {
          const key = billMonthKey(b);
          const t = effectiveTariff(b.parsed);
          return {
            month: key,
            economia: Math.round(Number(b.parsed?.credito_utilizado_kwh || 0) * t * 100) / 100,
            geracao: Number(b.parsed?.credito_utilizado_kwh || 0),
          };
        })
        .filter((x) => x.month)
        .sort((a, b) => a.month.localeCompare(b.month));

  // 5) CO₂ evitado (fator médio do SIN Brasil ~0.0817 kg CO₂/kWh)
  const CO2_FACTOR_KG_PER_KWH = 0.0817;
  const kwhForCo2 = useGeneration ? totalGen : totalSavingsKwh;
  const co2AvoidedKg = kwhForCo2 * CO2_FACTOR_KG_PER_KWH;
  const co2Trees = co2AvoidedKg / 22; // 1 árvore adulta absorve ~22 kg CO₂/ano
  const co2CarKm = co2AvoidedKg / 0.167; // ~167 g CO₂/km de um carro médio

  // 6) Exportar página como PDF (usa print nativo do navegador)
  const handleExportPdf = () => {
    window.print();
  };

  // 7) Compartilhar no WhatsApp — monta mensagem com os principais números
  const handleShareWhatsapp = () => {
    const pageUrl = window.location.href;
    const clientName = client?.name || "";
    const refMes = latest?.referencia_mes ? ` (${latest.referencia_mes})` : "";
    const lines = [
      `☀️ *Ramalho Gestão Energética*`,
      `Painel de *${clientName}*${refMes}`,
      ``,
    ];
    if (savingsLatest) {
      lines.push(`⚡ Geração da usina no ciclo: ${fmtKwh(savingsLatest.generation)}`);
      lines.push(
        `💰 Economia neste ciclo: ${fmtMoney(savingsLatest.economia)} (${savingsLatest.economiaPct.toFixed(1)}% de desconto)`
      );
      lines.push(``);
    }
    if (totalSavings > 0) {
      lines.push(`📈 *Totais acumulados*`);
      lines.push(`• Energia gerada: ${fmtKwh(totalSavingsKwh)}`);
      lines.push(`• Total economizado: ${fmtMoney(totalSavings)}`);
      if (co2AvoidedKg > 0) {
        const co2Str =
          co2AvoidedKg >= 1000
            ? `${fmtNumber(co2AvoidedKg / 1000, 2)} toneladas`
            : `${fmtNumber(co2AvoidedKg, 1)} kg`;
        lines.push(`• CO₂ evitado: ${co2Str} 🌱`);
      }
      lines.push(``);
    }
    lines.push(`Acompanhe em tempo real:`);
    lines.push(pageUrl);
    const message = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/?text=${message}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]" data-testid="client-public-page">
      {/* Client header */}
      <header className="bg-white border-b border-[#E5E5DF]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            {client.logo_path ? (
              <img
                src={publicFileUrl(client.logo_path)}
                alt={client.name}
                className="h-16 md:h-20 max-w-[220px] object-contain"
                data-testid="client-public-logo"
              />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-[#1B4332] text-white flex items-center justify-center font-display text-2xl font-semibold">
                {client.name?.[0] || "C"}
              </div>
            )}
            <div>
              <span className="overline text-[#52796F]">Painel de energia</span>
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#1A1A1A] mt-1">
                {client.name}
              </h1>
              {client.installation_number && (
                <p className="text-xs text-[#4A4A4A] mt-1">
                  Unidade Consumidora <span className="mono-num ml-1 text-[#1A1A1A]">{client.installation_number}</span>
                </p>
              )}
            </div>
          </div>
          {client.plant_capacity_kwp != null && (
            <div className="rounded-lg border border-[#E5E5DF] bg-[#F9F9F6] px-5 py-3">
              <div className="overline text-[#52796F]">Capacidade da usina</div>
              <div className="mono-num text-2xl font-semibold text-[#1B4332] mt-1">
                {client.plant_capacity_kwp} kWp
              </div>
            </div>
          )}
          <div className="print-hide flex flex-wrap items-center gap-2">
            <button
              onClick={handleShareWhatsapp}
              data-testid="share-whatsapp-btn"
              aria-label="Compartilhar no WhatsApp"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white hover:bg-[#1fb757] h-10 px-4 text-sm font-medium transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp
            </button>
            <Button
              onClick={handleExportPdf}
              data-testid="export-pdf-btn"
              className="rounded-full bg-[#1B4332] text-white hover:bg-[#122d22] h-10 px-5"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Exportar PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Top stats */}
        <section className="mb-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="overline text-[#52796F]">Resumo do último ciclo</span>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A1A1A] mt-1">
                {latest.referencia_mes ? `Referência ${latest.referencia_mes}` : "Aguardando dados"}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              testid="stat-consumo"
              label="Consumo"
              value={fmtKwh(latest.consumo_kwh).replace(" kWh", "")}
              unit="kWh"
              tone="default"
              icon={<Zap className="h-4 w-4 text-[#1B4332]" />}
            />
            <StatCard
              testid="stat-geracao"
              label="Geração"
              value={fmtKwh(latest.geracao_kwh ?? consolidated?.slice(-1)?.[0]?.generation).replace(" kWh", "")}
              unit="kWh"
              tone="accent"
              icon={<Sun className="h-4 w-4 text-[#1B4332]" />}
            />
            <StatCard
              testid="stat-saldo"
              label="Saldo de créditos"
              value={fmtKwh(latest.saldo_creditos_kwh).replace(" kWh", "")}
              unit="kWh"
              tone="primary"
              icon={<Battery className="h-4 w-4 text-[#1B4332]" />}
            />
            <StatCard
              testid="stat-total"
              label="Total da fatura"
              value={fmtMoney(latest.valores?.valor_total)}
              tone="default"
              icon={<Receipt className="h-4 w-4 text-[#1B4332]" />}
            />
          </div>
        </section>

        {/* Economia com energia solar - ciclo atual */}
        {savingsLatest && (
          <section className="mb-12" data-testid="savings-latest-section">
            <div className="relative overflow-hidden rounded-xl border border-[#1B4332]/20 bg-gradient-to-br from-[#1B4332] via-[#22503d] to-[#2d6a4f] text-white p-8 md:p-10">
              <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#D8F3DC]/10 blur-2xl pointer-events-none" />
              <div className="absolute bottom-0 right-8 opacity-10 pointer-events-none">
                <Sun className="h-40 w-40" />
              </div>

              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-[#D8F3DC]" />
                  <span className="overline text-[#D8F3DC]">Sua economia neste ciclo</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight max-w-2xl leading-tight">
                  Se você não tivesse energia solar, esta conta viria bem mais salgada.
                </h3>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
                  <div className="md:border-r md:border-white/15 md:pr-6">
                    <div className="overline text-[#D8F3DC]/80">Você pagou</div>
                    <div className="mono-num text-3xl md:text-4xl font-semibold mt-2" data-testid="savings-paid">
                      {fmtMoney(savingsLatest.valorAtual)}
                    </div>
                    <div className="text-xs text-[#D8F3DC]/70 mt-1">valor real da fatura EDP</div>
                  </div>
                  <div className="md:border-r md:border-white/15 md:pr-6">
                    <div className="overline text-[#D8F3DC]/80">Pagaria sem solar</div>
                    <div
                      className="mono-num text-3xl md:text-4xl font-semibold mt-2 text-[#F4B7A5] line-through decoration-[#E07A5F]/60 decoration-2"
                      data-testid="savings-without-solar"
                    >
                      {fmtMoney(savingsLatest.valorSem)}
                    </div>
                    <div className="text-xs text-[#D8F3DC]/70 mt-1">estimativa com toda a geração da usina</div>
                  </div>
                  <div>
                    <div className="overline text-[#D8F3DC]/80">Você economizou</div>
                    <div className="mono-num text-3xl md:text-4xl font-semibold mt-2 text-[#D8F3DC]" data-testid="savings-amount">
                      {fmtMoney(savingsLatest.economia)}
                    </div>
                    <div className="mono-num text-xs text-[#D8F3DC]/80 mt-1">
                      {savingsLatest.economiaPct.toFixed(1)}% de desconto no bolso
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/15 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#D8F3DC]/80">
                  <div className="flex items-center gap-2">
                    <Sun className="h-3.5 w-3.5" />
                    <span>
                      Geração da usina no ciclo:{" "}
                      <span className="mono-num text-white">{fmtKwh(savingsLatest.generation)}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PiggyBank className="h-3.5 w-3.5" />
                    <span>
                      Tarifa efetiva estimada:{" "}
                      <span className="mono-num text-white">R$ {savingsLatest.tarifaEfetiva.toFixed(4)}/kWh</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Chart */}
        <section className="mb-12">
          <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 md:p-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="overline text-[#52796F]">Histórico mensal</span>
                <h3 className="font-display text-xl md:text-2xl font-semibold text-[#1A1A1A] mt-1">
                  Geração vs Consumo
                </h3>
              </div>
              <TrendingUp className="h-5 w-5 text-[#52796F]" />
            </div>
            {consolidated && consolidated.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={consolidated}>
                    <defs>
                      <linearGradient id="gGen" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1B4332" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#1B4332" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gCons" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E07A5F" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#E07A5F" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E5E5DF" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tickFormatter={monthLabel}
                      tick={{ fontSize: 11, fill: "#4A4A4A" }}
                      axisLine={{ stroke: "#E5E5DF" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#4A4A4A" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #E5E5DF",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      labelFormatter={(l) => monthLabel(l)}
                      formatter={(v, n) => [`${Number(v).toLocaleString("pt-BR")} kWh`, n]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area
                      type="monotone"
                      dataKey="generation"
                      name="Geração"
                      stroke="#1B4332"
                      strokeWidth={2}
                      fill="url(#gGen)"
                    />
                    <Area
                      type="monotone"
                      dataKey="consumption"
                      name="Consumo"
                      stroke="#E07A5F"
                      strokeWidth={2}
                      fill="url(#gCons)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-[#4A4A4A] py-12 text-center">
                Ainda não há dados suficientes para gerar o histórico.
              </div>
            )}
          </div>
        </section>

        {/* Bar Chart: Monthly savings in R$ */}
        {monthlySavings.length > 0 && (
          <section className="mb-12" data-testid="monthly-savings-section">
            <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 md:p-8">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="overline text-[#52796F]">Economia mensal</span>
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-[#1A1A1A] mt-1">
                    Quanto sua usina te devolveu por mês
                  </h3>
                </div>
                <BarChart3 className="h-5 w-5 text-[#52796F]" />
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlySavings} margin={{ top: 20, right: 10, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="gSav" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2D6A4F" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#1B4332" stopOpacity={0.85} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E5E5DF" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tickFormatter={monthLabel}
                      tick={{ fontSize: 11, fill: "#4A4A4A" }}
                      axisLine={{ stroke: "#E5E5DF" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#4A4A4A" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `R$ ${Number(v).toLocaleString("pt-BR")}`}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #E5E5DF",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      cursor={{ fill: "#D8F3DC", opacity: 0.35 }}
                      labelFormatter={(l) => monthLabel(l)}
                      formatter={(v) => [fmtMoney(v), "Economia"]}
                    />
                    <Bar dataKey="economia" fill="url(#gSav)" radius={[8, 8, 0, 0]} maxBarSize={64}>
                      <LabelList
                        dataKey="economia"
                        position="top"
                        formatter={(v) => `R$ ${Math.round(Number(v))}`}
                        style={{ fill: "#1B4332", fontSize: 11, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-[#4A4A4A] mt-3">
                Cálculo: geração da usina × tarifa efetiva do mês (com impostos)
              </p>
            </div>
          </section>
        )}

        {/* Two-column: Impostos e Tarifas */}
        <section className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Impostos */}
          <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 md:p-8" data-testid="impostos-card">
            <span className="overline text-[#52796F]">Detalhamento</span>
            <h3 className="font-display text-xl md:text-2xl font-semibold text-[#1A1A1A] mt-1 mb-6">
              Impostos — último ciclo
            </h3>
            <div className="space-y-3">
              <TaxRow
                label="ICMS"
                percent={latest.impostos?.icms_percentual}
                value={latest.impostos?.icms_valor}
              />
              <TaxRow
                label="PIS"
                percent={latest.impostos?.pis_percentual}
                value={latest.impostos?.pis_valor}
              />
              <TaxRow
                label="COFINS"
                percent={latest.impostos?.cofins_percentual}
                value={latest.impostos?.cofins_valor}
              />
              <TaxRow label="CIP (Iluminação pública)" value={latest.impostos?.cip_valor} />
              <div className="pt-4 mt-4 border-t border-[#E5E5DF] flex items-baseline justify-between">
                <span className="overline text-[#52796F]">Impostos totais (histórico)</span>
                <span className="mono-num text-lg font-semibold text-[#1A1A1A]">{fmtMoney(totalTaxes)}</span>
              </div>
            </div>
          </div>

          {/* Tarifas */}
          <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 md:p-8" data-testid="tarifas-card">
            <span className="overline text-[#52796F]">Detalhamento</span>
            <h3 className="font-display text-xl md:text-2xl font-semibold text-[#1A1A1A] mt-1 mb-6">
              Tarifas — último ciclo
            </h3>
            <div className="space-y-3">
              <TariffRow label="TUSD" value={latest.tarifas?.tusd_kwh} unit="R$/kWh" decimals={5} />
              <TariffRow label="TE" value={latest.tarifas?.te_kwh} unit="R$/kWh" decimals={5} />
              <TariffRow label="Bandeira tarifária" text={latest.tarifas?.bandeira} />
              <TariffRow label="Adicional bandeira" value={latest.tarifas?.valor_bandeira} unit="R$" />
              <div className="pt-4 mt-4 border-t border-[#E5E5DF] flex items-baseline justify-between">
                <span className="overline text-[#52796F]">Créditos utilizados no ciclo</span>
                <span className="mono-num text-lg font-semibold text-[#1B4332]">
                  {fmtKwh(latest.credito_utilizado_kwh)}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Totals grid */}
        <section className="mb-12">
          <span className="overline text-[#52796F]">Totais acumulados</span>
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#1A1A1A] mt-1 mb-6">
            Sua jornada solar até aqui
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="total-gen" label="Geração total" value={fmtKwh(totalGen).replace(" kWh", "")} unit="kWh" tone="accent" />
            <StatCard testid="total-cons" label="Consumo total" value={fmtKwh(totalConsumption).replace(" kWh", "")} unit="kWh" />
            <StatCard testid="total-paid" label="Valor pago (histórico)" value={fmtMoney(totalPaid)} />
            <StatCard testid="total-taxes" label="Impostos pagos" value={fmtMoney(totalTaxes)} tone="warning" />
          </div>

          {totalSavings > 0 && (
            <div
              className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4"
              data-testid="savings-totals-section"
            >
              <StatCard
                testid="total-without-solar"
                label="Pagaria sem solar"
                value={fmtMoney(totalWithoutSolar)}
                hint="Estimativa considerando a geração total da usina"
                tone="warning"
                icon={<Receipt className="h-4 w-4 text-[#E07A5F]" />}
              />
              <StatCard
                testid="total-saved-kwh"
                label={useGeneration ? "Energia gerada pela usina" : "Créditos aplicados"}
                value={fmtKwh(totalSavingsKwh).replace(" kWh", "")}
                unit="kWh"
                hint={useGeneration ? "Total gerado no histórico" : "Créditos usados nas contas"}
                tone="accent"
                icon={<Sun className="h-4 w-4 text-[#1B4332]" />}
              />
              <StatCard
                testid="total-savings"
                label="Total economizado"
                value={fmtMoney(totalSavings)}
                hint={`${savingsPctAll.toFixed(1)}% de desconto acumulado`}
                tone="primary"
                icon={<PiggyBank className="h-4 w-4 text-[#1B4332]" />}
              />
            </div>
          )}

          {co2AvoidedKg > 0 && (
            <div
              className="mt-4 rounded-xl border border-[#1B4332]/15 bg-[#D8F3DC]/30 p-6 md:p-8"
              data-testid="co2-section"
            >
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-[#1B4332] text-[#D8F3DC] flex items-center justify-center flex-shrink-0">
                    <Leaf className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="overline text-[#1B4332]">Impacto ambiental</span>
                    <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#1A1A1A] mt-1">
                      Você evitou{" "}
                      <span className="text-[#1B4332] mono-num" data-testid="co2-kg">
                        {co2AvoidedKg >= 1000
                          ? `${fmtNumber(co2AvoidedKg / 1000, 2)} toneladas`
                          : `${fmtNumber(co2AvoidedKg, 1)} kg`}
                      </span>{" "}
                      de CO₂
                    </h3>
                    <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
                      Baseado no fator médio do Sistema Interligado Nacional (0,0817 kg CO₂/kWh) aplicado
                      sobre {fmtKwh(kwhForCo2)} de geração solar.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg bg-white border border-[#E5E5DF] p-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                    <Leaf className="h-4 w-4 text-[#1B4332]" />
                  </div>
                  <div>
                    <div className="mono-num text-2xl font-semibold text-[#1A1A1A]" data-testid="co2-trees">
                      {fmtNumber(co2Trees, 0)}
                    </div>
                    <div className="text-xs text-[#4A4A4A]">árvores adultas plantando por 1 ano</div>
                  </div>
                </div>
                <div className="rounded-lg bg-white border border-[#E5E5DF] p-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                    <Zap className="h-4 w-4 text-[#1B4332]" />
                  </div>
                  <div>
                    <div className="mono-num text-2xl font-semibold text-[#1A1A1A]" data-testid="co2-car-km">
                      {fmtNumber(co2CarKm, 0)} km
                    </div>
                    <div className="text-xs text-[#4A4A4A]">de carro médio deixaram de emitir</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Recent bills table */}
        {bills?.length > 0 && (
          <section className="mb-12">
            <span className="overline text-[#52796F]">Histórico</span>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#1A1A1A] mt-1 mb-6">
              Faturas recentes
            </h3>
            <div className="rounded-lg border border-[#E5E5DF] bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F9F6] border-b border-[#E5E5DF]">
                  <tr className="text-left">
                    <th className="p-4 overline text-[#52796F]">Ref.</th>
                    <th className="p-4 overline text-[#52796F]">Consumo</th>
                    <th className="p-4 overline text-[#52796F]">Créd. utiliz.</th>
                    <th className="p-4 overline text-[#52796F]">Saldo</th>
                    <th className="p-4 overline text-[#52796F] text-right">Pagaria sem solar</th>
                    <th className="p-4 overline text-[#52796F] text-right">Total pago</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((b) => {
                    const p = b.parsed || {};
                    const key = billMonthKey(b);
                    const genRec = (data.generation || []).find((g) => g.reference_month === key);
                    const genKwh =
                      Number(genRec?.generation_kwh || 0) ||
                      Number(p.geracao_kwh || 0) ||
                      Number(p.credito_utilizado_kwh || 0);
                    const t = billByMonth[key]?.tariff || avgTariff;
                    const valorAtual = Number(p.valores?.valor_total || 0);
                    const valorSem = genKwh > 0 && t > 0 ? valorAtual + genKwh * t : null;
                    return (
                      <tr key={b.id} className="border-t border-[#E5E5DF]">
                        <td className="p-4">{p.referencia_mes || "—"}</td>
                        <td className="p-4 mono-num">{fmtKwh(p.consumo_kwh)}</td>
                        <td className="p-4 mono-num">{fmtKwh(p.credito_utilizado_kwh)}</td>
                        <td className="p-4 mono-num text-[#1B4332] font-medium">{fmtKwh(p.saldo_creditos_kwh)}</td>
                        <td className="p-4 mono-num text-right text-[#B05A44] line-through decoration-[#E07A5F]/50">
                          {valorSem != null ? fmtMoney(valorSem) : "—"}
                        </td>
                        <td className="p-4 mono-num text-right font-semibold text-[#1B4332]">{fmtMoney(p.valores?.valor_total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      <footer className="border-t border-[#E5E5DF] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-16 flex items-center justify-center text-xs text-[#4A4A4A]">
          Powered by <span className="mx-1.5 font-medium text-[#1B4332]">Ramalho Gestão Energética</span>
        </div>
      </footer>
    </div>
  );
}

function TaxRow({ label, percent, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <div>
        <div className="text-sm text-[#1A1A1A]">{label}</div>
        {percent != null && (
          <div className="text-xs text-[#4A4A4A] mono-num">{Number(percent).toFixed(2)}%</div>
        )}
      </div>
      <div className="mono-num text-sm font-medium text-[#1A1A1A]">{fmtMoney(value)}</div>
    </div>
  );
}

function TariffRow({ label, value, unit, text, decimals = 2 }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className="text-sm text-[#1A1A1A]">{label}</div>
      <div className="mono-num text-sm font-medium text-[#1A1A1A]">
        {text ? (
          <span className="capitalize">{text}</span>
        ) : value != null ? (
          `${unit === "R$" ? "R$ " : ""}${Number(value).toFixed(decimals)}${unit && unit !== "R$" ? " " + unit : ""}`
        ) : (
          "—"
        )}
      </div>
    </div>
  );
}
